import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, CheckCircle2, Loader2, RotateCcw, Send, ThumbsDown, ThumbsUp } from "lucide-react";
import { format } from "date-fns";
import { th } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { addReply, getTicket, setSatisfaction, setStatus } from "../api/supportApi";
import { RichComposer } from "../components/RichComposer";
import { StatusPill, statusLabel } from "../components/meta";
import { sanitizeHtml, resolveMediaUrls, splitMessageMedia } from "../components/sanitize";
import type { TicketMessage } from "../types";

type T = Record<string, string>;

export function WidgetTicketView({
  ticketNo,
  onBack,
  t,
  language,
}: {
  ticketNo: string;
  onBack: () => void;
  t: T;
  language: string;
}) {
  const queryClient = useQueryClient();
  const [replyHtml, setReplyHtml] = useState("");
  const [replyText, setReplyText] = useState("");
  const [composerKey, setComposerKey] = useState(0);
  const endRef = useRef<HTMLDivElement>(null);

  const { data: ticket, isLoading } = useQuery({
    queryKey: ["support", "ticket", ticketNo],
    queryFn: () => getTicket(ticketNo),
    refetchInterval: 5000,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["support", "ticket", ticketNo] });
    queryClient.invalidateQueries({ queryKey: ["support", "tickets"] });
  };

  const replyMutation = useMutation({
    mutationFn: () => addReply(ticketNo, replyText, replyHtml),
    onSuccess: () => {
      setReplyHtml("");
      setReplyText("");
      setComposerKey((k) => k + 1);
      invalidate();
    },
  });
  const statusMutation = useMutation({
    mutationFn: (next: "Resolved" | "Open") => setStatus(ticketNo, next),
    onSuccess: invalidate,
  });
  const satisfactionMutation = useMutation({
    mutationFn: (rating: "up" | "down") => setSatisfaction(ticketNo, rating),
    onSuccess: invalidate,
  });

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [ticket?.messages.length]);

  const canSend = replyText.trim().length > 0 && !replyMutation.isPending;
  const isClosed = ticket?.status === "Resolved" || ticket?.status === "Closed";

  return (
    <div className="flex h-full flex-col">
      {/* Sub-header */}
      <div className="flex items-center gap-2 border-b border-gray-100 px-2 py-2 dark:border-gray-800">
        <button
          onClick={onBack}
          className="flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
            {ticket?.subject ?? "…"}
          </p>
          <p className="truncate font-mono text-[11px] text-gray-400 dark:text-gray-500">
            {ticketNo}
          </p>
        </div>
        {ticket && <StatusPill status={ticket.status} label={statusLabel(t, ticket.status)} />}
      </div>

      {/* Thread */}
      <div className="flex-1 space-y-3 overflow-y-auto p-3">
        {isLoading ? (
          <>
            <Skeleton className="h-14 w-2/3 rounded-2xl" />
            <Skeleton className="ml-auto h-14 w-2/3 rounded-2xl" />
          </>
        ) : (
          ticket?.messages.map((m) => (
            <Bubble key={m.id} message={m} t={t} language={language} />
          ))
        )}

        {isClosed && (
          <div className="pt-1">
            {ticket?.satisfaction ? (
              <div className="flex items-center justify-center gap-1.5 rounded-xl border border-emerald-100 bg-emerald-50/60 py-2 text-xs font-medium text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/[0.06] dark:text-emerald-300">
                <CheckCircle2 className="h-3.5 w-3.5" />
                {t.csatThanks}
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2 rounded-xl border border-gray-200 bg-white py-3 dark:border-gray-800 dark:bg-gray-900/40">
                <span className="text-xs font-medium text-gray-700 dark:text-gray-200">
                  {t.csatQuestion}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 rounded-full text-xs"
                    onClick={() => satisfactionMutation.mutate("up")}
                    disabled={satisfactionMutation.isPending}
                  >
                    <ThumbsUp className="h-3 w-3" />
                    {t.csatYes}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 rounded-full text-xs"
                    onClick={() => satisfactionMutation.mutate("down")}
                    disabled={satisfactionMutation.isPending}
                  >
                    <ThumbsDown className="h-3 w-3" />
                    {t.csatNo}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Composer */}
      <div className="space-y-2 border-t border-gray-100 p-2 dark:border-gray-800">
        <RichComposer
          key={composerKey}
          minHeightClass="min-h-[56px]"
          className="rounded-sm"
          placeholder={t.replyPlaceholder}
          onChange={(html, text) => {
            setReplyHtml(html);
            setReplyText(text);
          }}
        />
        <div className="flex items-center justify-between">
          {ticket &&
            (isClosed ? (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 rounded-full text-xs"
                onClick={() => statusMutation.mutate("Open")}
                disabled={statusMutation.isPending}
              >
                <RotateCcw className="h-3 w-3" />
                {t.reopen}
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 rounded-full text-xs"
                onClick={() => statusMutation.mutate("Resolved")}
                disabled={statusMutation.isPending}
              >
                <CheckCircle2 className="h-3 w-3" />
                {t.markResolved}
              </Button>
            ))}
          <Button
            size="sm"
            className="ml-auto h-7 rounded-full text-xs"
            onClick={() => replyMutation.mutate()}
            disabled={!canSend}
          >
            {replyMutation.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Send className="h-3 w-3" />
            )}
            {t.send}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Bubble({
  message,
  t,
  language,
}: {
  message: TicketMessage;
  t: T;
  language: string;
}) {
  const isAgent = message.senderType === "agent";
  return (
    <div className={cn("flex", isAgent ? "justify-start" : "justify-end")}>
      <div className="max-w-[85%]">
        <div className={cn("px-1 pb-0.5 text-[10px] text-gray-400", isAgent ? "text-left" : "text-right")}>
          {isAgent ? t.agentName : message.senderName} ·{" "}
          {format(new Date(message.sentOn), "d MMM HH:mm", {
            locale: language === "th" ? th : undefined,
          })}
        </div>
        {message.bodyHtml ? (
          (() => {
            const { textHtml, mediaHtml, hasText } = splitMessageMedia(
              sanitizeHtml(resolveMediaUrls(message.bodyHtml)),
            );
            return (
              <div className={cn("flex flex-col gap-1", isAgent ? "items-start" : "items-end")}>
                {hasText && (
                  <div
                    className={cn(
                      "rounded-sm px-3 py-2 text-sm leading-relaxed [&_ul]:list-disc [&_ul]:pl-4",
                      isAgent
                        ? "rounded-tl-[3px] bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100"
                        : "rounded-tr-[3px] bg-primary text-white",
                    )}
                    dangerouslySetInnerHTML={{ __html: textHtml }}
                  />
                )}
                {mediaHtml.map((m, i) => (
                  <div
                    key={i}
                    className="overflow-hidden rounded-lg [&_img]:block [&_img]:max-h-60 [&_img]:max-w-full [&_video]:block [&_video]:max-h-60 [&_video]:max-w-full"
                    dangerouslySetInnerHTML={{ __html: m }}
                  />
                ))}
              </div>
            );
          })()
        ) : (
          <div
            className={cn(
              "whitespace-pre-wrap rounded-sm px-3 py-2 text-sm leading-relaxed",
              isAgent
                ? "rounded-tl-[3px] bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100"
                : "rounded-tr-[3px] bg-primary text-white",
            )}
          >
            {message.body}
          </div>
        )}
      </div>
    </div>
  );
}
